import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO, { SEO_DATA } from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Check, ArrowRight, RefreshCw, Target, Lightbulb, TrendingUp } from 'lucide-react';

export default function CareerChangePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO {...SEO_DATA.careerChange} />
      <Header />
      
      <main className="pt-24 pb-20">
        {/* Hero */}
        <section className="bg-gradient-hero py-20">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <Badge className="bg-white/10 text-white border-white/20 mb-6">Career Transition</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit' }}>
              Resume Help for Career Changers
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
              Switching careers? Your resume needs to highlight transferable skills and reposition your experience for a new industry or role.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-6 text-lg"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* Challenges */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Career Change Resume Challenges
          </h2>
          
          <div className="space-y-4">
            {[
              { problem: 'Experience looks irrelevant at first glance', fix: 'We reframe your experience to highlight skills that transfer to your new target role.' },
              { problem: 'No idea how to position yourself', fix: 'We craft a professional summary that bridges your past and future careers.' },
              { problem: 'Job titles don\'t match target role', fix: 'We emphasize achievements and responsibilities that align with your new direction.' },
              { problem: 'Worried about being screened out', fix: 'We use industry keywords and clear positioning to help you pass initial screening.' },
            ].map((item, i) => (
              <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-amber-600 font-bold">?</span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 mb-1">{item.problem}</p>
                    <p className="text-slate-600 text-sm">→ {item.fix}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* How We Help */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Outfit' }}>
              How We Help Career Changers
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: RefreshCw, title: 'Skills Translation', desc: 'We identify and articulate transferable skills that apply to your new field.' },
                { icon: Target, title: 'Strategic Positioning', desc: 'Your resume will be strategically written for your target role, not your old one.' },
                { icon: Lightbulb, title: 'Story Reframing', desc: 'We help tell your career story in a way that makes the transition logical.' },
                { icon: TrendingUp, title: 'Highlight Growth', desc: 'We emphasize learning, adaptability, and relevant achievements.' },
                { icon: Check, title: 'Industry Keywords', desc: 'We incorporate keywords from your target industry to improve visibility.' },
                { icon: Check, title: 'Functional Balance', desc: 'We balance chronological experience with skills-focused content as needed.' },
              ].map((item, i) => (
                <Card key={i} className="p-6 rounded-2xl border border-slate-200">
                  <item.icon className="h-8 w-8 text-teal-600 mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Common Transitions */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Common Career Transitions We Help With
          </h2>
          
          <div className="grid md:grid-cols-2 gap-4">
            {[
              'Retail → Customer Service',
              'Military → Civilian',
              'Teaching → Corporate Training',
              'Hospitality → Office Administration',
              'Sales → Account Management',
              'Healthcare → Healthcare Administration',
              'Technical → Management',
              'Any field → New industry',
            ].map((transition, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl">
                <ArrowRight className="h-5 w-5 text-teal-600 flex-shrink-0" />
                <span className="text-slate-700">{transition}</span>
              </div>
            ))}
          </div>
          
          <p className="text-center text-slate-600 mt-8">
            Whatever your transition, we'll help you present your experience in the best possible light.
          </p>
        </section>

        {/* Tips */}
        <section className="bg-slate-100 py-16">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
              Career Change Resume Tips
            </h2>
            
            <div className="space-y-4">
              {[
                'Lead with a strong professional summary that bridges your experience to your goal',
                'Focus on achievements and skills rather than job titles and company names',
                'Include any relevant certifications, courses, or volunteer work in your new field',
                'Use language and keywords from job postings in your target industry',
                'Consider a skills-based or hybrid resume format instead of purely chronological',
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-xl">
                  <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">{tip}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Pages */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8" style={{ fontFamily: 'Outfit' }}>
            Explore More
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/for/entry-level" className="text-teal-700 hover:text-teal-800 underline">Entry-Level Jobs</Link>
            <span className="text-slate-300">|</span>
            <Link to="/services/resume-rewrite" className="text-teal-700 hover:text-teal-800 underline">Resume Rewrite Service</Link>
            <span className="text-slate-300">|</span>
            <Link to="/services/linkedin-optimization" className="text-teal-700 hover:text-teal-800 underline">LinkedIn Optimization</Link>
            <span className="text-slate-300">|</span>
            <Link to="/pricing" className="text-teal-700 hover:text-teal-800 underline">View Pricing</Link>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-6 py-16 text-center">
          <Card className="p-8 rounded-3xl bg-slate-900 text-white">
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              Ready to reposition your resume for a new career?
            </h3>
            <p className="text-slate-300 mb-6">
              Tell us about your current background and where you want to go. We'll help you make the transition.
            </p>
            <Button 
              onClick={() => navigate('/start')}
              className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8"
            >
              Start Your Intake <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
}
